import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { workspaceService } from '@/services/workspace';

export interface ActiveWorkspaceState {
    id: string;
    previousId: string | null;  // Track previous workspace for transition
    isActiveWorkspaceAdmin: boolean;
    isWorkspaceSwitching: boolean;
    status: 'idle' | 'loading' | 'succeeded' | 'failed';
    error: string | null;
}

// Helper function to get initial workspace from localStorage
const getInitialActiveWorkspace = (): string => {
    if (typeof window !== 'undefined') {
        const saved = localStorage.getItem('activeWorkspace');
        if (saved && saved !== 'undefined' && saved !== 'null' && saved.trim() !== '') {
            return saved;
        }
    }
    return '';
};

const initialState: ActiveWorkspaceState = {
    id: getInitialActiveWorkspace(),
    previousId: null,
    isActiveWorkspaceAdmin: false,
    isWorkspaceSwitching: false,
    status: 'idle',
    error: null
};

export const fetchIsActiveWorkspaceAdmin = createAsyncThunk(
    '/fetchIsActiveWorkspaceAdmin',
    async (workspaceId: string) => {
        const response = await workspaceService.isWorkspaceAdmin(workspaceId)
        return response
    }
)

export const activeWorkspaceSlice = createSlice({
    name: 'activeWorkspace',
    initialState,
    reducers: {
        setActiveWorkspace: (state, action: PayloadAction<string>) => {
            const workspaceId = action.payload;
            if (workspaceId && workspaceId !== 'undefined' && workspaceId !== 'null' && workspaceId.trim() !== '') {
                state.id = workspaceId;

                // Persist to localStorage
                if (typeof window !== 'undefined') {
                    localStorage.setItem('activeWorkspace', workspaceId);
                }
            } else {
                console.warn('setActiveWorkspace: Invalid workspace ID ignored:', workspaceId);
                state.id = '';

                // Remove from localStorage
                if (typeof window !== 'undefined') {
                    localStorage.removeItem('activeWorkspace');
                }
            }
        },
        // Use this for explicit workspace switching (e.g., from sidebar)
        // This sets the switching flag to prevent race conditions with auto-selection
        switchWorkspace: (state, action: PayloadAction<string>) => {
            const workspaceId = action.payload;
            if (workspaceId && workspaceId !== 'undefined' && workspaceId !== 'null' && workspaceId.trim() !== '') {
                // Store previous workspace for transition handling
                state.previousId = state.id;
                state.isWorkspaceSwitching = true;
                state.id = workspaceId;

                // Persist to localStorage
                if (typeof window !== 'undefined') {
                    localStorage.setItem('activeWorkspace', workspaceId);
                }
            }
        },
        // Call this after the switch transition is complete
        completeSwitchWorkspace: (state) => {
            state.isWorkspaceSwitching = false;
            state.previousId = null;
        },
        setIsActiveWorkspaceAdmin: (state, action: PayloadAction<boolean>) => {
            state.isActiveWorkspaceAdmin = action.payload;
        },
        setWorkspaceSwitching: (state, action: PayloadAction<boolean>) => {
            state.isWorkspaceSwitching = action.payload;
        },
    },
    extraReducers: (builder) => {
        builder.addCase(fetchIsActiveWorkspaceAdmin.pending, (state, action) => {
            state.status = 'loading'
        })
        builder.addCase(fetchIsActiveWorkspaceAdmin.fulfilled, (state, action) => {
            state.status = 'succeeded'
            state.isActiveWorkspaceAdmin = action.payload
        })
        builder.addCase(fetchIsActiveWorkspaceAdmin.rejected, (state, action) => {
            state.status = 'failed'
            state.error = action.error.message || null
        })
    }
});

export const { setActiveWorkspace, switchWorkspace, completeSwitchWorkspace, setIsActiveWorkspaceAdmin, setWorkspaceSwitching } = activeWorkspaceSlice.actions;
export default activeWorkspaceSlice.reducer;
